import { z } from 'zod';
import { zConfigApi, zClientDataApi, zDataApi, zListDataApi, zRouteToLocationDataApi, zHasTopMembers, zHasWorklistFunctionality, zHasPopover, zHasTitle, zHasWorkflow, zHasHideableCompanion, zHasUndoButton, zHasFlowStatus, zHasSubheaderDownloadLink, zHasLookBackPeriod, zIdentifierProps, zHasSubheaderErrorText, zHasFab, zOptionalIdentifierProps, zGroupingInfo, zHasConfigure, zHasFullHeight, zHasSummaries, zHasRangeSelector, } from './confdefnView';
import { CartItemType, AppType } from './general';
export const zExtraPivotParam = z.object({
    text: z.string(),
    dataApi: zDataApi,
});
export const zSubheaderDefnProps = z.object({
    groupBy: z.string().optional(),
    sortBy: z.string().optional(),
    rollup: z.string().optional(),
    pareDown: z.string().optional(),
    countLimit: z.string().optional(),
    breadcrumbs: z.array(z.string()).optional(),
    extraPivotParams: z.record(z.string(), zExtraPivotParam).optional(),
});
const DefnProps = z.object({
    model: z.string(),
    dataApi: zDataApi.optional(),
    assortmentModel: z.string().optional(),
    subheader: zSubheaderDefnProps,
    view: z.array(z.string()),
});
/** Some finagling to use in ConfigGrid for ExtraPivotParams without exceeding the type limit */
const DefnPropsSubheaderOnly = DefnProps.pick({
    subheader: true,
});
const MultiDefnProps = z.object({
    models: z.array(z.string()),
    assortmentModel: z.string().optional(),
    subheader: zSubheaderDefnProps,
    view: z.array(z.string().nullable()), // to accept null for mass edit defn
});
export const zBaseDefnsComponentProps = (title) => {
    return z.object(Object.assign(Object.assign({ defns: DefnProps }, zHasTitle(title).shape), zHasWorkflow.shape));
};
export const zBaseMultiDefnsComponentProps = (title) => {
    return z.object(Object.assign(Object.assign({ defns: MultiDefnProps }, zHasTitle(title).shape), zHasWorkflow.shape));
};
export const CollectionViewComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Collection View').shape), zIdentifierProps.shape), zHasPopover.shape), zHasFab.shape), zHasSubheaderDownloadLink.shape), zHasTopMembers.shape));
export const CanvasViewComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Canvas View').shape), zOptionalIdentifierProps.shape), zHasPopover.shape), zHasFab.shape), zHasWorklistFunctionality.shape), zHasFlowStatus.shape), zHasSubheaderDownloadLink.shape));
export const SummaryViewComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Summary View').shape), zOptionalIdentifierProps.shape), zHasPopover.shape), zHasFab.shape), zHasWorklistFunctionality.shape), zHasFlowStatus.shape), zHasSubheaderDownloadLink.shape), zHasTopMembers.shape));
export const GridViewComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Grid View').shape), zOptionalIdentifierProps.shape), zHasPopover.shape), zHasFab.shape), zHasWorklistFunctionality.shape), zHasFlowStatus.shape), zHasSubheaderDownloadLink.shape), zHasTopMembers.shape), zHasHideableCompanion.shape), zHasUndoButton.shape));
export const ColumnGroupedViewComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Floorset Comparison').shape), zIdentifierProps.shape), zGroupingInfo.shape), zHasPopover.shape), zHasFab.shape), zHasTopMembers.shape), zHasWorklistFunctionality.shape), zHasSubheaderDownloadLink.shape), { floorsetApi: zClientDataApi.optional() }));
export const QuickTrendsComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, zBaseMultiDefnsComponentProps('Quick Trends').shape), zHasTopMembers.shape), zHasLookBackPeriod.shape), zHasFlowStatus.shape), { graphDataApi: zListDataApi, gridDataApi: zListDataApi.optional(), floorsetApi: zClientDataApi.optional() }));
// TODO: config should be setup to be models since there are two.
// TODO: we may want to set this up like ListDataConfig (DataApi) but for FitView since that is what the params.aggBy field is really for.
// GroupingInfo is needed so pareto aggBy isnt hardcoded
export const zParetoAnalysisComponentProps = z.object(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Pareto Analysis').shape), zGroupingInfo.shape));
export const zParetoDetailsComponentProps = z.object(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Pareto Details').shape), zGroupingInfo.shape), { graphDataApi: zDataApi }));
export const NestedAttributeComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Nested Attribute').shape), zHasFlowStatus.shape), zHasSubheaderErrorText.shape), zHasConfigure.shape), zHasFullHeight.shape), zHasSummaries.shape), zHasTopMembers.shape), { noTreeColumnDefinition: z.boolean().optional().describe('This property is no longer used and can be removed') }));
export const zEditableOverTimeGridComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, zBaseMultiDefnsComponentProps('Flow Sheet').shape), zIdentifierProps.shape), zHasPopover.shape), zHasFab.shape), zHasFlowStatus.shape), zHasTopMembers.shape), zHasUndoButton.shape), zHasHideableCompanion.shape), { dataApi: zListDataApi.optional() }));
const zAssortmentAddDetailProps = z.object({
    level: z.string(),
    cartItemType: CartItemType,
    showSelectAll: z.boolean().optional().default(false),
});
const zAssortmentAddDetailPropsOptional = z.object({
    level: z.string().optional(),
    cartItemType: CartItemType.optional(),
    showSelectAll: z.boolean().optional().default(false),
});
export const zConfigurableGridApiProps = z.object({
    dataApi: zDataApi,
    topAttributesApi: zDataApi.optional(),
    configApi: zClientDataApi,
    planningApi: zClientDataApi,
    floorsetApi: zClientDataApi.optional(),
    isStyleColorEdit: z.boolean().optional(),
});
export const zConfigurableGridComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, zConfigurableGridApiProps.shape), { defns: DefnPropsSubheaderOnly.optional(), fabType: z.string().optional() }), zHasTopMembers.shape), zHasWorklistFunctionality.shape), zHasPopover.shape), zHasTitle('Assortment By Floorset').shape), zHasHideableCompanion.shape), zHasUndoButton.shape), zHasFlowStatus.shape), zHasSubheaderDownloadLink.shape), zHasLookBackPeriod.shape), zIdentifierProps.shape), zHasSubheaderErrorText.shape), zHasWorkflow.shape), { topMemberObj: z.record(z.string(), z.any()).optional(), showPublishText: z.boolean().optional(), publishText: z.string().optional(), publishAttribute: z.string().optional() }), zAssortmentAddDetailPropsOptional.shape));
export const zAssortmentAddViewComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Select Items').shape), zAssortmentAddDetailProps.shape), zHasFab.shape), zHasFlowStatus.shape), zHasRangeSelector.shape));
export const zAssortmentAddBySearchComponentProps = z.object(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Add Choices By Search').shape), zAssortmentAddDetailProps.shape), zHasFab.shape));
// FIXME: the above ConfigApi structure has different a shape and need to match the one in confdefnView.ts
const OvertimeConfigApi = z.object({
    params: z.object({
        appName: z.union([z.literal(AppType.Assortment), z.literal(AppType.TDAnalysis)]).optional(),
        defnId: z.string(),
        aggBy: z.string(),
        ignoreAncestors: z.boolean().optional(),
    }),
});
export const zNestedOvertimeComponentProps = z.object(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Nested Over Time').shape), { dataApi: OvertimeConfigApi, companionDataApi: OvertimeConfigApi.optional() }));
export const zAssortmentPublishComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Assortment Publish').shape), zIdentifierProps.shape), zHasSubheaderErrorText.shape), { dataApi: zListDataApi.optional() }));
export const zTargetListComponentProps = z.object({
    type: z.union([z.literal('Product'), z.literal('Location')]),
    view: z.array(z.string()),
});
export const zSizeEligibilityListGridComponentProps = z.object(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('').shape), zHasSubheaderDownloadLink.shape), { viewProperty: z.string(), floorsetApi: zConfigApi.optional(), dataApi: zListDataApi, companionApi: zListDataApi.optional() }));
export const WorklistComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Workflow').shape), zHasFab.shape), zHasFlowStatus.shape), zHasHideableCompanion.shape), { hideTitle: z.boolean().optional().default(true), showLevel: z.boolean().optional(), 
    /**
     * Provided to allow bindings to access the path of the default worklist tab
     * without retrieving the worklist viewdefn when building routes.
     *
     * This needs to match a configured worklist tab's `pathSlot` value in the `defns.view[0]`
     * */
    defaultPathSlot: z.string().optional() }));
export const zHistoryGridComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('List View').shape), zOptionalIdentifierProps.shape), zHasTopMembers.shape), zHasHideableCompanion.shape), zHasFab.shape), zHasFlowStatus.shape));
export const zTopPerformersComponentProps = z.object(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Top Performers').shape), zHasFlowStatus.shape), zHasWorklistFunctionality.shape));
export const ExceptionsSummaryComponentProps = z.object(Object.assign({ dataApi: zListDataApi, configApi: zConfigApi }, zHasWorkflow.shape));
export const zStyleEditComponentProps = z.object(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Style Details View').shape), zHasFab.shape));
export const EnhancedOvertimeComponentProps = z.object(Object.assign(Object.assign({}, zBaseMultiDefnsComponentProps('').shape), zHasTopMembers.shape));
export const zAssortmentCartViewComponentProps = z.object(Object.assign({}, zBaseDefnsComponentProps('').shape));
export const zProductivityComponentProps = z.object(Object.assign({}, zBaseDefnsComponentProps('Productivity').shape));
export const CategorySummaryComponentProps = z.object(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Quick Snapshot').shape), zHasTopMembers.shape));
export const ProductMixComponentProps = z.object(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Product Mix').shape), zHasTopMembers.shape));
export const StyleCollectionComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Style Collection').shape), zHasTopMembers.shape), zHasFlowStatus.shape), { collectionDataApi: zListDataApi, itemsDataApi: zListDataApi, 
    /** just in case a separate api is needed for the macros */
    macroDataApi: zListDataApi.optional() }));
export const zParameterTogglesComponentProps = z.object(Object.assign(Object.assign({}, zBaseDefnsComponentProps('').shape), zHasFab.shape));
export const zProductDetailsComponentProps = z.object(Object.assign({}, zBaseMultiDefnsComponentProps('Product Details').shape));
export const zRouteToLocationComponentProps = z.object({
    dataApi: zRouteToLocationDataApi,
});
export const zMacroMixComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign({}, zBaseDefnsComponentProps('Macro Mix').shape), zHasFlowStatus.shape), zHasLookBackPeriod.shape), { dataApi: zListDataApi, chartDataApi: zListDataApi }));
const zSummaryDataApiList = z.object({
    keyFinancialsDataApi: zListDataApi,
    choiceProductivityDataApi: zListDataApi,
    analysisDataApi: zListDataApi,
    productMixAndTrendDataApi: zListDataApi,
    geoTrendDataApi: zListDataApi,
});
export const zSummaryTopdownComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ defns: z.object({
        model: z.string(),
        subheader: zSubheaderDefnProps,
        view: z.array(z.string()),
    }) }, zHasTitle('Summary').shape), zHasFlowStatus.shape), zHasLookBackPeriod.shape), zSummaryDataApiList.shape), zHasWorkflow.shape));
export const zGeoTrendsComponentProps = z.object(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ dataApi: zListDataApi, graphDataApi: zListDataApi }, zHasLookBackPeriod.shape), zHasFlowStatus.shape), zHasTitle('Geo Trends').shape), zHasFlowStatus.shape), zHasTopMembers.shape), zHasWorkflow.shape), { defns: z.object({
        view: z.array(z.string()),
    }) }));
const zDimensionItem = z.object({
    dimension: z.string(),
    items: z.array(z.string()),
    hierarchy: z.string().optional(),
});
const zViewParams = z.object({
    viewParams: z.object({
        rows: z.array(zDimensionItem),
        columns: z.array(zDimensionItem),
        title: z.string(),
        relocalizeCurrency: z.boolean().optional().default(false),
        summaryMetrics: z.object({
            metrics: zDimensionItem,
            revisions: zDimensionItem,
        }),
    }),
});
const zSplitViewParams = z.object({
    viewParams: z.object({
        title: z.string(),
        relocalizeCurrency: z.boolean().optional().default(false),
        summaryMetrics: z.object({
            metrics: zDimensionItem,
            revisions: zDimensionItem,
        }),
        topGrid: z.object({
            rows: z.array(zDimensionItem),
            columns: z.array(zDimensionItem),
        }),
        bottomGrid: z.object({
            rows: z.array(zDimensionItem),
            columns: z.array(zDimensionItem),
        }),
    }),
});
const zSubheaderBtns = z.object({
    subheader: z.object({ extraBtns: z.array(z.string()).optional() }).optional(),
});
export const zMfpSummaryGridComponentProps = z.object(Object.assign(Object.assign(Object.assign({}, zHasTitle('Plan').shape), zViewParams.shape), zSubheaderBtns.shape));
export const zMfpSplitViewComponentProps = z.object(Object.assign(Object.assign(Object.assign({}, zHasTitle('Split View').shape), zSplitViewParams.shape), zSubheaderBtns.shape));
export const zMfpReviewPlansComponentProps = z.object(Object.assign(Object.assign({}, zHasTitle('Review Plans').shape), zViewParams.shape));
export const zPlanogramSimpleComponentProps = z
    .object(Object.assign({}, zHasTitle('Planogram Edit').shape))
    .loose();
const SMARTPLAN_BALANCE = z.literal('Balance');
const SMARTPLAN_SUMMARY = z.literal('Summary');
const SMARTPLAN_TARGETS = z.literal('Targets');
const SMARTPLAN_GRID = z.literal('Grid');
const SMARTPLAN_DESCRIPTION = z.literal('Description');
const SMARTPLAN_TOGGLE = z.literal('toggle');
const SMARTPLAN_PERCENT = z.literal('inputPercent');
const SMARTPLAN_SLIDER = z.literal('slider');
const SMARTPLAN_INPUTS = z.union([SMARTPLAN_TOGGLE, SMARTPLAN_PERCENT, SMARTPLAN_SLIDER]);
export const zPivotOptions = z.object({
    rows: z.array(zDimensionItem),
    columns: z.array(zDimensionItem),
    name: z.string().optional(),
    groupBy: z
        .object({
        dimension: z.string(),
        item: z.string(),
    })
        .optional(),
    readOnly: z.boolean(),
});
export const zSmartPlanTarget = z.object({
    /* Basic text label for the input */
    text: z.string(),
    /* Popover text input for the string, HTML not allowed */
    infoText: z.string(),
    /* MetricId tied to the input */
    dataIndex: z.string(),
    /* What editor will be used for the input */
    editor: SMARTPLAN_INPUTS,
});
const SMARTPLAN_COMPONENTS = z.union([
    SMARTPLAN_BALANCE,
    SMARTPLAN_SUMMARY,
    SMARTPLAN_TARGETS,
    SMARTPLAN_GRID,
    SMARTPLAN_DESCRIPTION,
]);
export const zSmartPlanSection = z.object({
    title: z.string(),
    icon: z.string(),
    targets: z.array(zSmartPlanTarget),
});
export const zMfpSmartPlanComponentProps = z.object(Object.assign({ 
    /* Section Title (not implemented yet) */
    title: z.string(), 
    /* Section Description */
    description: z.union([z.string(), z.null()]), 
    /* Component Type */
    component: SMARTPLAN_COMPONENTS, 
    /* Sections, leaves as empty array for non-target component */
    sections: z.union([z.null(), z.array(zSmartPlanSection)]), viewParams: z.union([zPivotOptions, z.null()]) }, zSubheaderBtns.shape));
export const zSmartPlanPlayload = z.object({
    steps: z.array(zMfpSmartPlanComponentProps),
});
export const zBulkImportComponentProps = z.object(Object.assign(Object.assign({}, zHasTitle('Bulk Import').shape), { importId: z.string(), stepper1Label: z.string().optional().default('Download a fresh template:'), stepper2Label: z.string().optional().default('Upload a completed template'), templateType: z.string().optional().default('import') }));
export const hasBaseDefns = (props) => {
    return 'defns' in props.componentProps && 'model' in props.componentProps.defns;
};
export const hasTargetListType = (props) => {
    return 'type' in props.componentProps;
};
export const hasImportId = (props) => {
    return 'importId' in props.componentProps;
};
export const hasBaseMultiDefns = (props) => {
    return 'defns' in props.componentProps && 'models' in props.componentProps.defns;
};
export const hasApiDefns = (props) => {
    return ('dataApi' in props.componentProps && 'configApi' in props.componentProps && 'planningApi' in props.componentProps);
};
export const hasViewParams = (props) => {
    return 'viewParams' in props.componentProps;
};
